<template>
  <AdminLayout>
    <Head title="Printers" />

    <template #pageTitle>Printers</template>

    <template #headingOptions>
      <button
        @click="showCreateModal"
        type="button"
        class="inline-flex items-center px-4 py-3 mr-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Create Printer
      </button>
    </template>

    <DataGrid
      name="printers_data_table"
      :endpoint="route('api.tables.printers')"
      rowId="id"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
      :table-context="modalContext"
    />

    <template #modals>
      <SmallModal
        :is-open="showModal"
        @close="closeModal"
      >
        <template #header>{{ modalLabel }} Printer</template>
        <div class="mt-4">
          <div class="input-group">
            <label for="name">Name</label>
            <input
              type="text"
              v-model="printerForm.name"
              name="name"
              id="name"
            />
            <p
              v-if="printerForm.errors.name"
              class="input-error"
            >
              {{ printerForm.errors.name }}
            </p>
          </div>
          <div class="input-group mt-2">
            <label for="source_id">Source ID</label>
            <input
              type="text"
              v-model="printerForm.source_id"
              name="source_id"
              id="source_id"
            />
            <p
              v-if="printerForm.errors.source_id"
              class="input-error"
            >
              {{ printerForm.errors.source_id }}
            </p>
          </div>
          <div class="input-group mt-2">
            <label for="integration">Integration</label>
            <select
              id="integration"
              v-model="printerForm.integration_id"
              name="integration"
              :disabled="modalAction === 'update'"
            >
              <option value="">Select integration</option>
              <option
                v-for="value in integrationOptions"
                :value="value.id"
                :key="value.id"
              >
                {{ value.display_name }}
              </option>
            </select>
            <p
              v-if="printerForm.errors.integration_id"
              class="input-error"
            >
              {{ printerForm.errors.integration_id }}
            </p>
          </div>
          <div class="input-group mt-2">
            <label for="location">Location</label>
            <select
              id="location"
              v-model="printerForm.location_id"
              name="location"
              :disabled="modalAction === 'update'"
            >
              <option value="">Select location</option>
              <option
                v-for="value in locationOptions"
                :value="value.id"
                :key="value.id"
              >
                {{ value.name }}
              </option>
            </select>
            <p
              v-if="printerForm.errors.location_id"
              class="input-error"
            >
              {{ printerForm.errors.location_id }}
            </p>
          </div>
        </div>
        <template #footer>
          <button
            type="button"
            @click="submitForm"
            :disabled="printerForm.processing"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
          >
            {{ modalLabel }}
          </button>
          <button
            type="button"
            class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-gray-100 text-base font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 sm:ml-3 sm:w-auto sm:text-sm"
            @click="closeModal"
          >
            Cancel
          </button>
        </template>
      </SmallModal>
    </template>
  </AdminLayout>
</template>

<script setup>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, useForm } from '@inertiajs/vue3'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { computed, onBeforeMount, ref } from 'vue'
import ActionCell from '@/Components/DataGrid/Cells/ActionCell.vue'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'

const props = defineProps({
  errors: Object,
  integrationOptions: Array,
  locationOptions: Array
})

const { context } = useInitGridState()

const showModal = ref(false)
const modalContext = ref(null)
const modalAction = ref('create')
const selectedPrinter = ref(null)
const printerForm = useForm({
  name: null,
  source_id: null,
  integration_id: null,
  location_id: null
})

const form = useForm({})
const dataGridState = ref()

const columns = ref([
  {
    headerName: 'Name',
    field: 'name',
    sort: 'asc'
  },
  {
    headerName: 'Location',
    field: 'location',
    valueFormatter: (params) => params.value.name,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.locationOptions,
      keyCreator: (params) => params.value.id,
      valueFormatter: (params) => params.value.name
    }
  },
  {
    headerName: 'Integration',
    field: 'integration',
    valueFormatter: (params) => params.value.display_name,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: props.integrationOptions,
      keyCreator: (params) => params.value.id,
      valueFormatter: (params) => params.value.display_name
    }
  },
  {
    headerName: 'Source ID',
    field: 'source_id',
    sortable: false,
    filter: 'agTextColumnFilter'
  },
  {
    headerName: 'Actions',
    cellRenderer: ActionCell,
    cellRendererParams: (params) => ({
      options: {
        updateAction: 'update',
        deleteAction: 'delete'
      }
    }),
    sortable: false,
    width: 125,
    maxWidth: 125,
    minWidth: 125,
    resizable: false
  }
])

const modalLabel = computed(() => {
  const modalActions = {
    create: 'Create',
    update: 'Update'
  }
  return modalActions[modalAction.value]
})

const runAction = (actionName, rowData) => {
  if (actionName === 'update') {
    showUpdateModal(rowData)
  }
  if (actionName === 'delete') {
    deletePrinter(rowData)
  }
}

const showCreateModal = () => {
  printerForm.reset()
  modalAction.value = 'create'
  showModal.value = true
}

const showUpdateModal = (printer) => {
  selectedPrinter.value = printer
  printerForm.name = printer.name
  printerForm.source_id = printer.source_id
  printerForm.integration_id = printer.integration.id
  printerForm.location_id = printer.location.id
  modalAction.value = 'update'
  showModal.value = true
}

const submitForm = () => {
  if (modalAction.value === 'create') {
    createPrinter()
  }
  if (modalAction.value === 'update') {
    updatePrinter()
  }
}

const createPrinter = () => {
  printerForm.post(route('company.printers.store'), {
    onSuccess: () => {
      closeModal()
      context.refresh()
    }
  })
}

const updatePrinter = () => {
  printerForm.put(route('company.printers.update', selectedPrinter.value.id), {
    onSuccess: () => {
      closeModal()
      context.refresh()
    }
  })
}

const deletePrinter = async (printer) => {
  const { show } = useConfirmDialog()
  const [error, data] = await settle(
    show({
      title: 'Delete Printer',
      body: 'Are you sure you want to delete ' + printer.name + '? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete'
    })
  )
  if (!error) {
    printerForm.delete(route('company.printers.destroy', printer.id), {
      onSuccess: () => {
        context.refresh()
      }
    })
  }
}

const closeModal = () => {
  showModal.value = false
  selectedPrinter.value = null
  printerForm.reset()
  printerForm.clearErrors()
}

onBeforeMount(() => {
  modalContext.value = {
    actionFunction: runAction
  }
})
</script>
